import { useState, useEffect } from 'react';
import { Profile, Event } from '@/lib/types/core';
import { appsAdvApiRouterListRides, RideOut } from '@/lib/api';

// API function to fetch user events
const fetchUserEvents = async (userId: string): Promise<Event[]> => {
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
  
  // Fetch events using the user ID directly
  const eventsResponse = await fetch(`${API_BASE_URL}/api/profiles/${userId}/events/`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!eventsResponse.ok) {
    throw new Error(`Failed to fetch user events: ${eventsResponse.status}`);
  }

  const events = await eventsResponse.json();
  return events.map((event: any) => ({
    id: event.id,
    event_type: event.event_type,
    title: event.title,
    description: event.description,
    meeting_latitude: event.meeting_latitude,
    meeting_longitude: event.meeting_longitude,
    meeting_datetime: event.meeting_datetime,
    kickstands_up_time: event.kickstands_up_time,
    terrain_difficulty: event.terrain_difficulty,
    rider_skill_level: event.rider_skill_level,
    ride_type: event.ride_type,
    gpx_link: event.gpx_link,
    image_url: event.image_url,
    video_embed_url: event.video_embed_url,
    ticket_tiers: event.ticket_tiers,
    registration_form_fields: event.registration_form_fields,
    host_user_id: event.host_user_id,
    revenue_split: event.revenue_split,
    created_by: event.created_by,
    created_at: event.created_at,
  }));
};

// API function to fetch user profile by ID
const fetchUserProfile = async (userId: string): Promise<Profile> => {
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
  
  const response = await fetch(`${API_BASE_URL}/api/profiles/${userId}/`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user profile: ${response.status}`);
  }

  const userData = await response.json();
  
  // Map API user to Profile type
  return {
    id: userData.id,
    username: userData.username,
    email: userData.email,
    first_name: userData.first_name,
    last_name: userData.last_name,
    avatar_url: userData.avatar_url || '',
    bio: userData.bio || '',
    location: userData.location || '',
    geo_location: userData.geo_location || null,
    preferred_riding_style: userData.preferred_riding_style || '',
    role: userData.role,
    created_at: userData.created_at,
    email_verified: userData.email_verified,
  };
};

// Hook to fetch user profile
export const useUserProfile = (userId: string | undefined) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) {
        setLoading(false);
        setError('User ID is missing');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const profileData = await fetchUserProfile(userId);
        setProfile(profileData);
      } catch (error: any) {
        console.error('Error fetching profile:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  return { profile, loading, error };
};

// Hook to fetch user events
export const useUserEvents = (userId: string | undefined) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      if (!userId) {
        setError('User ID is missing');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const eventsData = await fetchUserEvents(userId);
        setEvents(eventsData);
      } catch (error: any) {
        console.error('Error fetching user events:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [userId]);

  return { events, loading, error };
};

// Hook to fetch user rides
export const useUserRides = (userId: string | undefined, rideTimeFilter: 'Future' | 'Past' | 'All' = 'Future') => {
  const [rides, setRides] = useState<RideOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRides = async () => {
      if (!userId) {
        setError('User ID is missing');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get rides created by this user, defaulting to future rides
        const response = await appsAdvApiRouterListRides({
          creator_or_rsvp_user: userId,
          time_filter: rideTimeFilter // Default to future rides
        });
        setRides(response.data.results);
      } catch (error: any) {
        console.error('Error fetching user rides:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRides();
  }, [userId]);

  return { rides, loading, error };
};

// Combined hook for profile, events, and rides
export const useUserProfileAndEvents = (userId: string | undefined) => {
  const { profile, loading: profileLoading, error: profileError } = useUserProfile(userId);
  const { events, loading: eventsLoading, error: eventsError } = useUserEvents(userId);
  const { rides, loading: ridesLoading, error: ridesError } = useUserRides(userId);
  const { rides: rideHistory, loading: rideHistoryLoading, error: rideHistoryError } = useUserRides(userId, 'Past');

  const loading = profileLoading || eventsLoading || ridesLoading;
  const error = profileError || eventsError || ridesError;

  return {
    profile,
    events,
    rides,
    rideHistory,
    loading,
    error,
  };
}; 